/* eslint-disable @typescript-eslint/member-ordering */
/* eslint-disable @angular-eslint/directive-selector */
import {
  Directive,
  ElementRef,
  OnInit,
  HostListener,
  Input,
  Renderer2,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { ListGroupState, ListItemState, ListState } from '@usf/ngrx-list';
import { ListDetailManagementService } from '../../lists/shared/list-detail-management/services/list-detail-management.service';
import { PlatformService } from '@panamax/app-state';
@Directive({
  selector: '[lmaDragDropItem]',
})
/**
 * This directive marks an element as draggable, it will add some of the following attributes to the element:
 * listId,
 * productNumber,
 * productSequenceNumber,
 * groupId,
 * groupName,
 * groupSequenceNumber
 */
export class DragDropItemDirective implements OnInit, OnChanges {
  @Input() item: any;
  @Input() isChecked: boolean;
  /** Use the renderer to add attributes to the elRef ([lmaDragDropItem] element) */
  constructor(private elRef: ElementRef, private rd: Renderer2) {}
  ngOnInit() {
    this.setProductAttributes();
    this.setGroupAttributes();
  }

  /** Virtual Scrolling bindings need to be recreated */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.item && !changes.item.firstChange) {
      this.item = changes.item.currentValue;
      this.setProductAttributes();
      this.setGroupAttributes();
    }
  }
  /** Add product related attributes on the parent element for drag and drop ops */
  private setProductAttributes() {
    if (this.item?.summary) {
      this.rd.setAttribute(
        this.elRef.nativeElement,
        'productNumber',
        this.item.summary?.productNumber?.toString(),
      );
      this.rd.setAttribute(
        this.elRef.nativeElement,
        'sequenceNumber',
        this.item?.groupSequenceNumber,
      );
    }
  }
  /** Add Group related attributes on the parent element for drag and drop ops */
  private setGroupAttributes() {
    if (this.item?.item?.listGroupKey) {
      this.rd.setAttribute(
        this.elRef.nativeElement,
        'groupName',
        this.item?.groupName,
      );
      this.rd.setAttribute(
        this.elRef.nativeElement,
        'groupId',
        this.item?.item?.listGroupKey?.listGroupId,
      );
    } else {
      this.rd.setAttribute(
        this.elRef.nativeElement,
        'groupName',
        this.item?.groupName,
      );
      this.rd.setAttribute(
        this.elRef.nativeElement,
        'groupId',
        this.item?.listGroupKey?.listGroupId,
      );
    }
  }
}
@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[lmaDragDropContainer]',
})
/**
 * This marks an element as a container for drag and drop operations of
 * elements marked-up with DragDropItemDirective
 */
export class DragDropContainerDirective implements OnInit, OnDestroy {
  @Input() listItems: ListItemState[];
  @Input() groups: any[];
  @Input() list: ListState;
  /** Emit when drop event is fired */
  // @Output() dragDrop: EventEmitter<any> = new EventEmitter();
  // #region - Private Vars
  private interval: any;
  /** Used for controlling how fast scrolling should happen */
  private currentScrollFactor: number;
  /** Indicates the User is dragging */
  private dragActive: boolean;
  /** Contains the [lmaDragDropItem] element last hovered over */
  private currentDropElement: Element;
  /** Dynamically created element that gets displayed when dragging */
  private dragImageTag: HTMLElement;
  /** Indicates the User is dragging up, useful for dropping products into the first and last list position */
  private movingUp: boolean;
  /** Tracks the previous vertical position of mouse */
  private previousY = 0;
  /** currentDropElement Attribute 'productNumber' */
  private targetProductNumber: string;
  /** currentDropElement Attribute 'index' */
  private targetIndex: string;
  /** currentDropElement Attribute 'groupId' */
  private targetGroupId: string;
  /** currentDropElement Attribute 'groupName' */
  private targetGroupName: string;
  /** The [lmaDragDropItem] being dragged 'productNumber' */
  private sourceProductNumber: string;
  /** the [lmaDragDropItem] being dragged 'index' */
  private sourceIndex: string;
  /** The [lmaDragDropItem] being dragged 'groupId' */
  private sourceGroupId: string;
  /** The [lmaDragDropItem] being dragged 'groupName' */
  private sourceGroupName: string;
  /** The virtual scroll viewport or 'window' */
  private scrollableElement: any = window;
  /** Store selected products */
  private selectedProductIds: number[];
  /** Track state of the mouse button up/down */
  private mouseDown = false;
  /** Touch Variables */
  private touchTarget: EventTarget;
  private moveTouch: Touch;
  private productSelectionFooter: HTMLElement;
  // private touchOffset: number = 100;
  private touchMoveInterval: any;
  private touchmoveListenFunc: any;
  private touchendListenFunc: any;
  private touchcancelListenFunc: any;
  // #endregion
  /** ElRef is used to make logical desisions based on the elemtent */
  constructor(
    private elRef: ElementRef,
    private platformService: PlatformService,
    private rd: Renderer2,
    private listManagementService: ListDetailManagementService,
  ) {}
  ngOnInit() {
    /** Create the image that's displayed when dragging products */
    this.createSvgTag();
  }

  // #region - Touch Events
  private onTouchStart(event: TouchEvent) {
    this.touchTarget = event.target;
    if (
      !this.onDragStart(
        this.touchTarget as HTMLElement,
        event.touches[0].clientY,
      )
    ) {
      return;
    }
    this.moveTouch = event.touches[0];
  }
  private onTouchMove(event: TouchEvent) {
    if (!this.selectedProductIds?.includes(Number(this.sourceProductNumber))) {
      return;
    }
    if (this.mouseDown) {
      this.dragActive = true;
    }
    this.moveTouch = event.touches[0];
    if (this.touchMoveInterval) {
      return;
    }
    event.preventDefault();
    this.touchMoveInterval = setInterval(() => {
      const element = document.elementFromPoint(
        this.moveTouch.clientX,
        this.moveTouch.clientY,
      );
      this.onDrag(element, this.moveTouch.clientY, this.moveTouch.clientX);
    }, 100);
  }
  private onTouchEnd(event: TouchEvent) {
    if (!this.dragActive) {
      return;
    }
    clearInterval(this.touchMoveInterval);
    this.touchMoveInterval = undefined;
    if (!this.moveTouch) {
      return;
    }
    this.onDragEnd(
      document.elementFromPoint(this.moveTouch.screenX, this.moveTouch.screenY),
    );
    this.moveTouch = null;
    this.touchTarget = null;
  }
  // @HostListener('touchcancel', ['$event'])
  private onTouchCancel(event: TouchEvent) {
    this.onTouchEnd(event);
  }
  // #endregion
  // #endregion
  private onDragStart(srcElement: Element, clientY: number): boolean {
    this.productSelectionFooter = document.getElementById(
      'product-selection-footer',
    );
    // Ensure that we found a suitable source and that it is selected to start the drag.
    if (!this.setSource(srcElement)) {
      return;
    }
    this.productSelectionFooter.classList.add('disable-footer-pointer-events');
    this.setScrollPosition(clientY);
    this.startScrollDwell();
    return true;
  }
  // #region - Mouse Events
  @HostListener('touchstart', ['$event'])
  @HostListener('mousedown', ['$event'])
  onMouseDown(event: any) {
    event.stopPropagation();
    this.selectedProductIds = this.listManagementService.getSelectedProductNumbers(
      this.listItems,
    );

    if (this.selectedProductIds?.length < 1) {
      return;
    }
    // Touch support
    if (event.touches) {
      this.onTouchStart(event);
      this.removePreviousTouchListeners(); // memory leaks
      this.touchmoveListenFunc = this.rd.listen(
        event.target,
        'touchmove',
        e => {
          this.onTouchMove(e);
        },
      );
      this.touchendListenFunc = this.rd.listen(event.target, 'touchend', e => {
        this.removePreviousTouchListeners();
        this.onTouchEnd(e);
      });
      this.touchcancelListenFunc = this.rd.listen(
        event.target,
        'touchcancel',
        e => {
          this.removePreviousTouchListeners();
          this.onTouchCancel(e);
        },
      );
      this.mouseDown = true;
      return;
    }
    this.mouseDown = true;
    this.onDragStart(event.target as HTMLElement, event.clientY);
  }
  @HostListener('mousemove', ['$event'])
  @HostListener('mouseover', ['$event'])
  onMouseMove(event: MouseEvent) {
    // Fix for Edge event.buttons intermittently reports 0
    //   const buttonTest = (/Edge/.test(navigator.userAgent) ||
    //   /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || event.buttons === 1);
    // if (this.mouseDown && buttonTest && !this.ngRedux.getState().isShowAllGroups) {
    if (!this.selectedProductIds?.includes(Number(this.sourceProductNumber))) {
      return;
    }
    if (this.mouseDown) {
      this.dragActive = true;
      this.onDrag(event.target as Element, event.clientY, event.clientX);
    } else {
      this.mouseDown = this.dragActive = false;
      this.onDragEnd(null);
    }
  }
  @HostListener('mouseup', ['$event'])
  onMouseUp(event: MouseEvent) {
    this.mouseDown = false;
    if (this.dragActive) {
      this.dragActive = false;
      this.onDragEnd(event.target as HTMLElement);
    }
    this.dragActive = false;
  }
  // #endregion

  private onDrag(srcElement: Element, clientY: number, clientX: number) {
    this.scrollableElement =
      srcElement?.closest('.cdk-virtual-scroll-viewport') || window;
    this.dragImageTag.style.top = `${clientY - 10}px`;
    this.dragImageTag.style.left = `${clientX - 10}px`;
    this.setScrollPosition(clientY);
    this.setTarget(srcElement);
  }

  private onDragEnd(srcElement: Element) {
    this.productSelectionFooter.classList.remove(
      'disable-footer-pointer-events',
    );
    this.clearCurrentDropElement();
    if (
      (!this.targetProductNumber && !this.targetGroupId) ||
      (!this.targetProductNumber && this.targetGroupId === this.sourceGroupId)
    ) {
      this.resetDragDropProperties();
      return;
    }
    // Call service to move products
    let sourceGroup: ListGroupState;
    let targetGroup: ListGroupState;
    const listItems: ListItemState[] = [];
    this.groups.forEach(group => {
      if (group?.listGroupKey?.listGroupId === this.sourceGroupId) {
        sourceGroup = group;
      }
      if (group?.listGroupKey?.listGroupId === this.targetGroupId) {
        targetGroup = group;
      }
    });
    this.listItems.forEach(item => {
      if (item?.isSelected) {
        listItems.push(item);
      }
    });
    let tempTarget = Number(this.targetIndex);
    tempTarget--;

    if (!this.selectedProductIds.includes(Number(this.targetProductNumber))) {
      if (this.targetGroupId === this.sourceGroupId) {
        this.listManagementService.moveItemsWithinGroup(
          this.list,
          sourceGroup,
          listItems,
          tempTarget,
          this.movingUp,
        );
      } else {
        this.listManagementService.moveItemsToAnotherGroup(
          this.list,
          targetGroup,
          sourceGroup,
          listItems,
          this.listItems,
          true,
        );
      }
    }

    this.resetDragDropProperties();
  }

  /** Reset properties related to drag and drop */
  resetDragDropProperties() {
    this.dragImageTag.style.display = 'none';
    this.currentDropElement = null;
    this.sourceGroupId = null;
    this.targetGroupId = null;
    this.sourceGroupName = null;
    this.targetIndex = null;
    this.sourceIndex = null;
    this.targetGroupName = null;
    this.sourceProductNumber = null;
    this.targetProductNumber = null;
    this.movingUp = null;
    this.previousY = null;
    clearInterval(this.interval);
  }

  /** Used the set properties other functions use to determine if user is scrolling up/down and to set scroll speed */
  private setScrollPosition(clientY: number) {
    // Determine of we're inserting above or below the current element.
    if (Math.abs(clientY - this.previousY) > 5) {
      this.movingUp = clientY < this.previousY;
      this.previousY = clientY;
    }
    if (clientY < 200) {
      this.currentScrollFactor = -40;
      return;
    }
    if (clientY < 225) {
      this.currentScrollFactor = -10;
      return;
    }
    if (clientY < 250) {
      this.currentScrollFactor = -2;
      return;
    }
    if (window.innerHeight - clientY < 100) {
      this.currentScrollFactor = 40;
      return;
    }
    if (window.innerHeight - clientY < 125) {
      this.currentScrollFactor = 10;
      return;
    }
    if (window.innerHeight - clientY < 150) {
      this.currentScrollFactor = 2;
      return;
    }
    this.currentScrollFactor = 0;
  }
  /** Return the closest [lmaDragDropItem] element (if it exists) */
  private getDragSource(srcElement: Element): Element {
    return srcElement?.closest('[lmaDragDropItem]');
  }
  /** Scroll automatically in the window or a virtual scroll element */
  private startScrollDwell() {
    // Time in milliseconds
    const dwellTime = 10;
    this.interval = setInterval(() => {
      if (this.mouseDown) {
        this.scrollableElement.scrollBy(0, this.currentScrollFactor);
      } else {
        clearInterval(this.interval);
      }
    }, dwellTime);
  }
  /** Store the meta-data about the element being dragged */
  private setSource(srcElement: Element): string {
    this.currentDropElement = this.getDragSource(srcElement);
    if (!this.currentDropElement) {
      return;
    }
    this.sourceProductNumber = this.currentDropElement.getAttribute(
      'productNumber',
    );
    if (!this.selectedProductIds.includes(Number(this.sourceProductNumber))) {
      return;
    }
    this.sourceGroupId = this.currentDropElement.getAttribute('groupId');
    this.sourceGroupName = this.currentDropElement.getAttribute('groupName');
    this.sourceIndex = this.currentDropElement.getAttribute('sequenceNumber');
    return this.sourceProductNumber;
  }
  private setTarget(srcElement: Element): string {
    this.clearCurrentDropElement();
    this.currentDropElement = this.getDragSource(srcElement);
    if (!this.currentDropElement) {
      return;
    }
    this.targetProductNumber = this.currentDropElement.getAttribute(
      'productNumber',
    );
    this.targetGroupId = this.currentDropElement.getAttribute('groupId');
    this.targetGroupName = this.currentDropElement.getAttribute('groupName');
    this.targetIndex = this.currentDropElement.getAttribute('sequenceNumber');
    this.updateSvgTag(
      this.sourceGroupName !== this.targetGroupName ? this.targetGroupName : '',
    );
    // If we're over the start position, not reason to show drop zone.
    if (this.sourceProductNumber === this.targetProductNumber) {
      return this.targetProductNumber;
    }
    // Show the drop zones.
    if (this.currentDropElement.children[0]) {
      if (this.targetIndex) {
        this.currentDropElement.children[0].classList.add(
          this.movingUp ? 'drop-zone-up' : 'drop-zone-down',
        );
      } else {
        if (this.sourceGroupName !== this.targetGroupName) {
          this.currentDropElement.classList.add('drag-drop-aura');
        }
      }
    } else if (this.currentDropElement && this.platformService.isTouch.value) {
      if (this.sourceGroupName !== this.targetGroupName) {
        this.currentDropElement.classList.add('drag-drop-aura');
      }
    }
    this.dragImageTag.style.display = 'block';
    return this.targetProductNumber;
  }

  private clearCurrentDropElement() {
    if (this.currentDropElement && this.currentDropElement.children[0]) {
      this.currentDropElement.children[0].classList.remove('drop-zone-up');
      this.currentDropElement.children[0].classList.remove('drop-zone-down');
      this.currentDropElement.classList.remove('drag-drop-aura');
    }
    if (this.currentDropElement && this.platformService.isTouch.value) {
      this.currentDropElement.classList.remove('drag-drop-aura');
    }
  }
  /** Update the displayed ImageSVG */
  private updateSvgTag(group: string) {
    this.dragImageTag.innerHTML = this.buildDragImageSvg(group);
  }
  /** House the ImageSVG that will be dragged around */
  private createSvgTag() {
    this.dragImageTag = document.createElement('div');
    this.dragImageTag.style.position = 'fixed';
    this.dragImageTag.style.zIndex = '1021';
    this.dragImageTag.style.top = '300px';
    this.dragImageTag.style.left = '400px';
    this.dragImageTag.style.pointerEvents = 'none';
    this.elRef.nativeElement.appendChild(this.dragImageTag);
    this.dragImageTag.style.display = 'none';
    this.updateSvgTag('');
  }
  /** Create html and SVG elements to display as dragging */
  private buildDragImageSvg(group: string): string {
    if (!this.selectedProductIds) {
      return;
    }
    let productToGroupExpression =
      this.selectedProductIds.length === 1
        ? `${this.selectedProductIds.length} product`
        : `${this.selectedProductIds.length} products`;
    if (group) {
      productToGroupExpression += ` to ${group}`;
    }

    return `
        <div style="background-color:white;margin-top:-25px;color:red">Moving ${productToGroupExpression}</div>
          <svg style="opacity:.8" width="175" height="40" xmlns="http://www.w3.org/2000/svg">
          <g>
          <title>background</title>
          <rect fill="none" id="canvas_background" height="9.547168" width="35.018859" y="-1" x="-1"/>
          </g>
          <g>
          <title>Layer 1</title>
          <rect fill="#fff" stroke-width="1.5" x="0.897209" y="0.473432" width="173.428553" height="39.428567" id="svg_1" rx="2"
          stroke="#9eadb8"/>
          <rect fill="#9eadb8" stroke-width="1.5" x="2.165505" y="1.456011" width="1.965157" height="37.289193" id="svg_2"
          stroke="#9eadb8"/>
          <line fill="none" stroke="#000000" stroke-width="0.4" x1="173.928553" y1="20.982141" x2="157.928553" y2="20.982141" id="svg_3"
          stroke-linejoin="null" stroke-linecap="null" />
          <rect fill="none" stroke="#000000" stroke-width="0.4" stroke-opacity="null" fill-opacity="null" x="137.928558" y="16.410712"
          width="17.142855" height="8.857142" id="svg_4" rx="2"/>
          <line fill="none" stroke="#000000" stroke-width="0.4" stroke-opacity="null" fill-opacity="null" x1="11.564554" y1="13.217745"
          x2="50" y2="13.217745" id="svg_11" stroke-linejoin="null" stroke-linecap="null" stroke-dasharray="5,2,2,2,2,2"/>
          <line fill="none" stroke="#000000" stroke-width="0.4" stroke-opacity="null" fill-opacity="null" x1="11.564554" y1="20.314516"
          x2="50" y2="20.314516" id="svg_16" stroke-linejoin="null" stroke-linecap="null" stroke-dasharray="5,2,2,2"/>
          <line fill="none" stroke="#000000" stroke-width="0.4" stroke-opacity="null" fill-opacity="null" x1="11.564554" y1="27.153222"
          x2="50" y2="27.153222" id="svg_17" stroke-linejoin="null" stroke-linecap="null" stroke-dasharray="5,2,2,2,2,2"/>
          </g>
          </svg>
      `;
  }
  private removePreviousTouchListeners() {
    if (this.touchmoveListenFunc) {
      this.touchmoveListenFunc();
    }
    if (this.touchendListenFunc) {
      this.touchendListenFunc();
    }
    if (this.touchcancelListenFunc) {
      this.touchcancelListenFunc();
    }
    this.touchmoveListenFunc = null;
    this.touchendListenFunc = null;
    this.touchcancelListenFunc = null;
  }
  ngOnDestroy() {
    this.removePreviousTouchListeners();
  }
}
